import { i18n } from '@i18n/translations';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@neogeniux/uikit';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Copy,
  Home,
  RefreshCw,
} from 'lucide-react';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { captureException } from '../../../../sentry-client';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

function generateEventId(): string {
  // Short random reference the operator can share with the admin so
  // the admin can grep server logs / Sentry. Not cryptographic —
  // just needs to be unique enough within a session.
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
  ).toUpperCase();
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const eventId = generateEventId();

    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    this.setState({ error, errorInfo, eventId });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    captureException(error, {
      tags: { source: 'ErrorBoundary', eventId },
      extra: {
        componentStack: errorInfo.componentStack,
        url: window.location.href,
      },
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    const { hasError, error, errorInfo, eventId } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) return fallback;

      return (
        <ErrorFallbackUI
          error={error}
          errorInfo={errorInfo}
          eventId={eventId}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return children;
  }
}

interface ErrorFallbackUIProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
  onReset: () => void;
  onGoHome: () => void;
}

const ErrorFallbackUI: React.FC<ErrorFallbackUIProps> = ({
  error,
  errorInfo,
  eventId,
  onReset,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);

  const errorReport = React.useMemo(() => {
    if (!error) return '';
    const lines = [
      `Ref: ${eventId ?? '—'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `Message: ${error.message}`,
    ];
    if (error.stack) lines.push('', 'Stack:', error.stack);
    if (errorInfo?.componentStack)
      lines.push('', 'Component stack:', errorInfo.componentStack);
    return lines.join('\n');
  }, [error, errorInfo, eventId]);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(errorReport);
      toast.success(t(i18n.COMMON.ERROR_REPORT_COPIED));
    } catch {
      toast.error(t(i18n.COMMON.SOMETHING_WENT_WRONG));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 dark:bg-red-950/40 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl">
              {t(i18n.COMMON.SOMETHING_WENT_WRONG)}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-muted-foreground">
            {t(i18n.COMMON.ERROR_REPORTED)}
          </p>

          {eventId && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium">
                {t(i18n.COMMON.ERROR_REFERENCE)}:
              </span>
              <code className="font-mono px-2 py-0.5 rounded bg-muted text-foreground">
                {eventId}
              </code>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={onReset} className="flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t(i18n.COMMON.TRY_AGAIN)}
            </Button>
            <Button variant="outline" onClick={onGoHome} className="flex-1">
              <Home className="h-4 w-4 mr-2" />
              {t(i18n.COMMON.GO_HOME)}
            </Button>
          </div>

          {error && (
            <div className="pt-4 border-t space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetails((v) => !v)}
                >
                  {showDetails ? (
                    <ChevronUp className="h-4 w-4 mr-1.5" />
                  ) : (
                    <ChevronDown className="h-4 w-4 mr-1.5" />
                  )}
                  {showDetails
                    ? t(i18n.COMMON.HIDE_ERROR)
                    : t(i18n.COMMON.SHOW_ERROR)}
                </Button>
                {showDetails && (
                  <Button variant="ghost" size="sm" onClick={handleCopy}>
                    <Copy className="h-4 w-4 mr-1.5" />
                    {t(i18n.COMMON.COPY_ERROR_DETAILS)}
                  </Button>
                )}
              </div>

              {showDetails && (
                <div className="space-y-2">
                  <div className="p-3 rounded-md bg-muted border">
                    <p className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
                      {error.message}
                    </p>
                  </div>
                  {error.stack && (
                    <div className="p-3 rounded-md bg-muted border overflow-auto max-h-64">
                      <p className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                        {error.stack}
                      </p>
                    </div>
                  )}
                  {errorInfo?.componentStack && (
                    <div className="p-3 rounded-md bg-muted border overflow-auto max-h-64">
                      <p className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                        {errorInfo.componentStack}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {t(i18n.COMMON.CONTACT_SUPPORT)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  return <ErrorBoundaryClass {...props} />;
};

export default ErrorBoundary;
